import { CompanyDto } from '@jaam/commons/dtos';

interface RouteConfig {
  path: string;
  roles: string[];
  experimentalMode?: boolean;
}

interface UseRouteAccessOptions {
  routes: RouteConfig[];
  defaultRedirect?: string;
  onUnauthorized?: () => void;
  activeCompany?: CompanyDto | null;
  location: string;
}

export const useRouteAccess = ({
  routes,
  defaultRedirect = '/home',
  onUnauthorized,
  activeCompany,
  location,
}: UseRouteAccessOptions) => {
  let hasAccess = true;
  let fallbackRoute: string | null = defaultRedirect;
  if (!activeCompany) {
    hasAccess = false;
  } else {
    // Trouve la route qui correspond exactement au début du chemin ou est suivie d'un '/'
    const currentRoute = routes.find((item) => {
      // Échapper les caractères spéciaux dans item.path pour le regex
      // const escapedPath = item.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedPath = item.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Créer le regex: ^escapedPath(?:/|$)
      // ^        : Début de la chaîne (location)
      // escapedPath: Le chemin de la route, échappé
      // (?:/|$) : Groupe non-capturant qui correspond soit à un '/' soit à la fin de la chaîne
      const regex = new RegExp(`^${escapedPath}(?:/|$)`);
      return regex.test(location);
    });

    if (currentRoute) {
      // Check if route should be hidden based on experimental mode
      const shouldHideRoute =
        currentRoute.experimentalMode !== undefined &&
        currentRoute.experimentalMode !== activeCompany?.experimentalMode;

      if (shouldHideRoute) {
        const fallbackRouteTmp = routes.find(
          (item) =>
            (item.experimentalMode === undefined ||
              item.experimentalMode === activeCompany?.experimentalMode) &&
            (item.roles.length === 0 ||
              item.roles.some((roleName) =>
                activeCompany?.roles?.some((role) => role.name === roleName),
              )),
        );
        fallbackRoute = fallbackRouteTmp?.path || defaultRedirect;
        hasAccess = false;

        if (onUnauthorized) {
          onUnauthorized();
        }
      } else {
        const routeHasAccess =
          currentRoute.roles.length === 0 ||
          currentRoute.roles.some((roleName) =>
            activeCompany?.roles?.some((role) => role.name === roleName),
          );

        if (!routeHasAccess) {
          const fallbackRouteTmp = routes.find(
            (item) =>
              (item.experimentalMode === undefined ||
                item.experimentalMode === activeCompany?.experimentalMode) &&
              (item.roles.length === 0 ||
                item.roles.some((roleName) =>
                  activeCompany?.roles?.some((role) => role.name === roleName),
                )),
          );
          fallbackRoute = fallbackRouteTmp?.path || defaultRedirect;
          hasAccess = false;

          if (onUnauthorized) {
            // Note: Calling side effects like onUnauthorized here might be unexpected
            // if the component re-renders multiple times.
            onUnauthorized();
          }
        } else {
          hasAccess = true;
        }
      }
    }
    // If no route matches, access is generally allowed or handled elsewhere
    // Keeping hasAccess = true by default unless a specific route denies it.
  }

  return {
    hasAccess,
    fallbackRoute,
  };
};
